import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAllCompanySubscriptions, extendTrialPeriod } from '../../api/subscription';
import { toast } from 'react-toastify';
import Loader from '../../component/Loader';
import BackButton from '../../component/BackButton';
import moment from 'moment';

function CompanySubscriptionDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [expandedSection, setExpandedSection] = useState(null);
  const [extendingTrial, setExtendingTrial] = useState(null);
  const [trialExtensionDays, setTrialExtensionDays] = useState(7);

  useEffect(() => {
    fetchSubscriptionDetails();
  }, [id]);

  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      const response = await getAllCompanySubscriptions(id);
      
      if (response?.code === 1 && response?.data) {
        setCompanyData(response.data.company);
        setSubscriptions(response.data.subscriptions || []);
      } else {
        setCompanyData(null);
        setSubscriptions([]);
        toast.info('No company found or no subscriptions available');
      }
    } catch (error) {
      console.error('Error fetching subscription details:', error);
      toast.error('Error fetching subscription details');
      setCompanyData(null);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const calculateTrialDaysLeft = (trialEndDate) => {
    if (!trialEndDate) return null;
    const now = moment();
    const trialEnd = moment(trialEndDate);
    const daysLeft = trialEnd.diff(now, 'days');
    return daysLeft >= 0 ? daysLeft : 0;
  };

  const handleExtendTrial = async (subscriptionId) => {
    try {
      setExtendingTrial(subscriptionId);
      
      // Call API to extend trial
      const response = await extendTrialPeriod({
        subscription_id: subscriptionId,
        extension_days: trialExtensionDays
      });
      
      if (response?.code === 1) {
        toast.success(`Trial extended by ${trialExtensionDays} days successfully`);
        // Refresh subscription data
        await fetchSubscriptionDetails();
      } else {
        toast.error(response?.message || 'Failed to extend trial');
      }
    } catch (error) {
      console.error('Error extending trial:', error);
      toast.error('Error extending trial period');
    } finally {
      setExtendingTrial(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'active': { color: 'success', icon: 'fa-check-circle' },
      'trialing': { color: 'warning', icon: 'fa-clock' },
      'cancelled': { color: 'danger', icon: 'fa-times-circle' },
      'past_due': { color: 'danger', icon: 'fa-exclamation-triangle' },
      'incomplete': { color: 'secondary', icon: 'fa-minus-circle' }
    };

    const config = statusConfig[status?.toLowerCase()] || statusConfig['incomplete'];
    return (
      <span className={`badge bg-${config.color} me-2`}>
        <i className={`fa ${config.icon} me-1`}></i>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
      </span>
    );
  };

  if (loading) {
    return (
      <article className="col-12 col-lg-9 articleClass">
        <Loader />
      </article>
    );
  }

  return (
    <article className="col-12 col-lg-9 articleClass">
      <div className="article-body store-bd-gy">
        <div className="row align-items-center col-rever">
          <div className="col-12">
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center mb-2 mb-md-0">
                <BackButton />  
                {companyData && (
                      <span className="ms-2 ms-md-3" style={{color: '#2c59ac'}}>
                        {companyData.company_name}
                      </span>
                )} 
              </div>
              <div className="text-center text-md-end">
                <h3 className="mb-0">
                  Subscription Details
                </h3>
              </div>
            </div>
          </div>
        </div>

        {subscriptions.length === 0 ? (
          <div className="text-center py-5">
            <span className="fa fa-crown fa-4x text-muted mb-3"></span>
            <h4 className="text-muted">No Subscriptions Found</h4>
            <p className="text-muted">This company doesn't have any subscriptions.</p>
          </div>
        ) : (
          <div className="row mt-4">
            <div className="col-12">
              {/* Multiple Subscriptions Accordion */}
              <div className="accordion" id="subscriptionAccordion">
                {subscriptions.map((subscription, index) => (
                  <div key={subscription.id} className="accordion-item mb-3">
                    <h2 className="accordion-header" id={`subscriptionHeader${index}`}>
                      <button
                        className={`accordion-button ${expandedSection === `subscription${index}` ? '' : 'collapsed'}`}
                        type="button"
                        onClick={() => toggleSection(`subscription${index}`)}
                      >
                        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between w-100 gap-2">
                          <div className="d-flex align-items-center">
                            <span className="fa fa-crown me-2" style={{color: '#2c59ac'}}></span>
                            <strong className="me-2">{subscription.plan?.plan_name || 'Unknown Plan'}</strong>
                          </div>
                          <div className="d-flex flex-column flex-md-row align-items-center gap-2 text-md-end">
                            <span className="text-muted small">
                              {moment(subscription.createdAt).format('MMM DD, YYYY')}
                            </span>
                            {getStatusBadge(subscription.status)}
                          </div>
                        </div>
                      </button>
                    </h2>
                    <div
                      className={`accordion-collapse collapse ${expandedSection === `subscription${index}` ? 'show' : ''}`}
                      id={`subscriptionCollapse${index}`}
                    >
                      <div className="accordion-body">
                        {/* Subscription Basic Information */}
                        <div className="row mb-4">
                          <div className="col-12">
                            <h5 className="mb-3" style={{color: '#2c59ac'}}>
                              <span className="fa fa-info-circle me-2"></span>
                              Subscription Information
                            </h5>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label text-muted">Subscription ID</label>
                              <p className="form-control-plaintext">{subscription.stripe_subscription_id || 'N/A'}</p>
                            </div>
                            <div className="mb-3">
                              <label className="form-label text-muted">Customer ID</label>
                              <p className="form-control-plaintext">{subscription.stripe_customer_id || 'N/A'}</p>
                            </div>
                            <div className="mb-3">
                              <label className="form-label text-muted">Status</label>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                {getStatusBadge(subscription.status)}
                                {subscription.status === 'trialing' && subscription.trial_end && (
                                  <span className="badge bg-info flex-shrink-0">
                                    <i className="fa fa-clock me-1"></i>
                                    {calculateTrialDaysLeft(subscription.trial_end)} days left
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label text-muted">Started Date</label>
                              <p className="form-control-plaintext">
                                {subscription.current_period_start 
                                  ? moment(subscription.current_period_start).format('MMMM DD, YYYY')
                                  : 'N/A'
                                }
                              </p>
                            </div>
                            <div className="mb-3">
                              <label className="form-label text-muted">Next Billing Date</label>
                              <p className="form-control-plaintext">
                                {subscription.current_period_end 
                                  ? moment(subscription.current_period_end).format('MMMM DD, YYYY')
                                  : 'N/A'
                                }
                              </p>
                            </div>
                            <div className="mb-3">
                              <label className="form-label text-muted">Trial Information</label>
                              {subscription.trial_end ? (
                                <div>
                                  <p className="form-control-plaintext mb-2">
                                    <strong>End Date:</strong> {moment(subscription.trial_end).format('MMMM DD, YYYY')}
                                  </p>
                                  {subscription.status === 'trialing' && calculateTrialDaysLeft(subscription.trial_end) > 0 && (
                                    <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
                                     
                                      <div className="d-flex align-items-center gap-2 flex-grow-1">
                                        <input
                                          type="number"
                                          className="form-control form-control-sm"
                                          placeholder="Days"
                                          min="1"
                                          max="90"
                                          value={trialExtensionDays}
                                          onChange={(e) => setTrialExtensionDays(Math.min(90, Math.max(1, parseInt(e.target.value) || 1)))}
                                        />
                                      </div>
                                      <button
                                        className="btn btn-sm flex-shrink-0"
                                        style={{backgroundColor: '#2c59ac', borderColor: '#2c59ac', color: 'white'}}
                                        onClick={() => handleExtendTrial(subscription.id)}
                                        disabled={extendingTrial === subscription.id}
                                      >
                                        {extendingTrial === subscription.id ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm me-1"></span>
                                            Extending...
                                          </>
                                        ) : (
                                          <>
                                            Extend Trial Days
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="form-control-plaintext">No trial period</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Plan Details */}
                        <div className="row mb-4">
                          <div className="col-12">
                            <h5 className="mb-3" style={{color: '#2c59ac'}}>
                              <span className="fa fa-crown me-2"></span>
                              Plan Details
                            </h5>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label text-muted">Plan Name</label>
                              <p className="form-control-plaintext">{subscription.plan?.plan_name || 'N/A'}</p>
                            </div>
                            <div className="mb-3">
                              <label className="form-label text-muted">Description</label>
                              <p className="form-control-plaintext">{subscription.plan?.description || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label text-muted">Price</label>
                              <p className="form-control-plaintext">
                                £{subscription.plan?.price || '0'} / {subscription.plan?.billing_interval || 'month'}
                              </p>
                            </div>
                            <div className="mb-3">
                              <label className="form-label text-muted">Student Limit</label>
                              <p className="form-control-plaintext">
                                {subscription.plan?.student_limit || 'Unlimited'} students
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Add-ons Section */}
                        <div className="row mb-4">
                          <div className="col-12">
                            <h5 className="mb-3" style={{color: '#2c59ac'}}>
                              <span className="fa fa-puzzle-piece me-2"></span>
                              Add-ons ({subscription.addons?.length || 0})
                            </h5>
                          </div>
                          {subscription.addons && subscription.addons.length > 0 ? (
                            <div className="col-12">
                              <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-3">
                                {subscription.addons.map((addon, addonIndex) => (
                                  <div key={addonIndex} className="col">
                                    <div className="card h-100" style={{borderColor: '#2c59ac'}}>
                                      <div className="card-body">
                                        <h6 className="card-title">
                                          <span className="fa fa-puzzle-piece me-2" style={{color: '#2c59ac'}}></span>
                                          {addon.addon?.addon_name || 'Unknown Addon'}
                                        </h6>
                                        <div className="card-text">
                                          <p className="mb-2">
                                            <strong>Price:</strong> £{addon.addon?.price || '0'}
                                          </p>
                                          <p className="mb-2">
                                            <strong>Description:</strong> {addon.addon?.description || 'No description'}
                                          </p>
                                          <p className="mb-0">
                                            <strong>Stripe Item ID:</strong> 
                                            <span className="d-block text-break small">
                                              {addon.stripe_subscription_item_id || 'N/A'}
                                            </span>
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="col-12">
                              <div className="text-center py-3 bg-light rounded">
                                <span className="fa fa-puzzle-piece fa-2x text-muted mb-2"></span>
                                <p className="text-muted mb-0">No add-ons active for this subscription</p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Billing Information */}
                        <div className="row">
                          <div className="col-12">
                            <h5 className="mb-3" style={{color: '#2c59ac'}}>
                              <span className="fa fa-credit-card me-2"></span>
                              Billing Information
                            </h5>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label text-muted">Current Period Start</label>
                              <p className="form-control-plaintext">
                                {subscription.current_period_start 
                                  ? moment(subscription.current_period_start).format('MMMM DD, YYYY HH:mm')
                                  : 'N/A'
                                }
                              </p>
                            </div>
                            <div className="mb-3">
                              <label className="form-label text-muted">Current Period End</label>
                              <p className="form-control-plaintext">
                                {subscription.current_period_end 
                                  ? moment(subscription.current_period_end).format('MMMM DD, YYYY HH:mm')
                                  : 'N/A'
                                }
                              </p>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label text-muted">Trial End</label>
                              <p className="form-control-plaintext">
                                {subscription.trial_end 
                                  ? moment(subscription.trial_end).format('MMMM DD, YYYY HH:mm')
                                  : 'No trial period'
                                }
                              </p>
                            </div>
                            <div className="mb-3">
                              <label className="form-label text-muted">Days Until Next Billing</label>
                              <p className="form-control-plaintext">
                                {subscription.current_period_end 
                                  ? Math.max(0, moment(subscription.current_period_end).diff(moment(), 'days')) + ' days'
                                  : 'N/A'
                                }
                              </p>
                            </div>
                          </div>
                          {subscription.payments && subscription.payments.length > 0 && (
                            <div className="col-12 mt-2">
                              <label className="form-label text-muted mb-2 d-block">Invoices</label>
                              <div className="table-responsive">
                                <table className="table table-sm align-middle mb-0">
                                  <tbody>
                                    {subscription.payments.map((payment, idx) => {
                                      const invoiceName = payment.stripe_invoice_id
                                        ? `Invoice ${payment.stripe_invoice_id}`
                                        : `Payment #${payment.id ?? '—'}`;
                                      const created = payment.created_at || payment.paid_at;
                                      return (
                                        <tr key={payment.id ?? payment.stripe_invoice_id ?? `payment-row-${idx}`}>
                                          <td>{invoiceName}</td>
                                          <td>
                                            {created
                                              ? moment(created).format('MMMM DD, YYYY HH:mm')
                                              : '—'}
                                          </td>
                                          <td className="text-end">
                                            {payment.invoice_pdf ? (
                                              <a
                                                href={payment.invoice_pdf}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-decoration-none"
                                              >
                                                <span className="fa fa-file-pdf-o me-1" aria-hidden="true"></span>
                                                View
                                              </a>
                                            ) : (
                                              <span className="text-muted small">—</span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

export default CompanySubscriptionDetails;
